import * as React from 'react';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shigo-ui/ui';
import { cn } from '@shigo-ui/ui/cn';

import { DashboardDataTable } from './DashboardDataTable';
import type { DashboardDataTableProps } from './DashboardDataTable.types';

export const dashboardTableDialogEmbeddedTableClassName = 'gap-3';
export const dashboardTableDialogEmbeddedSurfaceClassName = 'shadow-none!';

export type DashboardTableDialogDataTableProps<TItem> = Omit<
  DashboardDataTableProps<TItem>,
  'title' | 'tableLabel'
> & {
  tableLabel: string;
};

export function DashboardTableDialogDataTable<TItem>({
  className,
  surfaceClassName,
  tableLabel,
  ...props
}: DashboardTableDialogDataTableProps<TItem>) {
  return (
    <DashboardDataTable
      {...props}
      className={cn(dashboardTableDialogEmbeddedTableClassName, className)}
      surfaceClassName={cn(dashboardTableDialogEmbeddedSurfaceClassName, surfaceClassName)}
      tableLabel={tableLabel}
    />
  );
}

export function DashboardTableDialogContent({
  className,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  return (
    <DialogContent
      className={cn(
        'max-h-[90vh] max-w-[min(1280px,calc(100vw-2rem))] gap-0 overflow-hidden p-0',
        className,
      )}
      {...props}
    />
  );
}

export interface DashboardTableDialogHeaderProps {
  description?: string | undefined;
  title: React.ReactNode;
}

export function DashboardTableDialogHeader({
  description,
  title,
}: DashboardTableDialogHeaderProps) {
  const accessibleDescription = description ?? (typeof title === 'string' ? title : undefined);

  return (
    <DialogHeader className="border-border shrink-0 border-b px-5 py-3.5">
      <DialogTitle className="pr-8 text-[15px] leading-5 font-semibold">{title}</DialogTitle>
      {accessibleDescription ? (
        <DialogDescription className="sr-only">{accessibleDescription}</DialogDescription>
      ) : null}
    </DialogHeader>
  );
}

export function DashboardTableDialogBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('min-h-0 min-w-0 overflow-auto p-5 sm:max-h-[72vh]', className)}
      {...props}
    />
  );
}

export function DashboardTableDialogFooter({
  className,
  ...props
}: React.ComponentProps<typeof DialogFooter>) {
  return (
    <DialogFooter
      className={cn('border-border shrink-0 border-t px-5 py-3', className)}
      {...props}
    />
  );
}
